import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Info, Settings } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '@shared/components/ui/Toaster'; 

interface LocationState {
  from?: { pathname: string };
}

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user, isConfigured } = useAuth();
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      const needsSubscription = localStorage.getItem('new_user_needs_subscription');

      if (needsSubscription === 'true') {
        localStorage.removeItem('new_user_needs_subscription');
        console.log('🔐 Login: New user detected, redirecting to subscription page');
        navigate('/subscription', { replace: true });
        toast.info('Welcome! Please select a subscription plan to get started.');
        return;
      }

      const destination = (location.state as LocationState)?.from?.pathname || '/dashboard';
      console.log('🔐 Login: User already authenticated, redirecting to:', destination);
      navigate(destination, { replace: true });
    }

    // Check for stale parameter only once on mount
    const params = new URLSearchParams(location.search);
    if (params.get('stale') === 'true') {
      toast.info('Your session expired. Please log in again.');
    }
  }, [user]);

  // Check for redirect location
  const from = (location.state as LocationState)?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConfigured) {
      setError('Application is not properly configured. Please contact support.');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      await signIn(email, password);
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific error messages including network errors
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('Network error')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error?.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (error?.message) {
        setError(error.message);
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show configuration error if not configured
  if (!isConfigured) {
    return (
      <div className="py-12">
        <div className="container max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Settings className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold">Configuration Error</h1>
            <p className="text-muted-foreground mt-2">
              Environment variables are not properly configured
            </p>
          </div>

          <div className="bg-white rounded-xl border border-border/40 p-8">
            <div className="bg-red-50 text-red-700 rounded-md p-4 mb-6 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Supabase Configuration Missing</p>
                <p className="text-sm mt-1">
                  The application cannot connect to the database because environment variables are missing or invalid.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-md p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Required Environment Variables:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <code>VITE_SUPABASE_URL</code> - Your Supabase project URL</li>
                  <li>• <code>VITE_SUPABASE_ANON_KEY</code> - Your Supabase anonymous key</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 rounded-md p-4">
                <p className="text-sm font-medium text-amber-800 mb-2">For Netlify Deployment:</p>
                <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Go to your Netlify site dashboard</li>
                  <li>Navigate to Site settings → Environment variables</li>
                  <li>Add the required variables with your Supabase credentials</li>
                  <li>Redeploy your site</li>
                </ol>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="flex-1"
              >
                Refresh Page
              </Button>
            </div>
            
            <div className="mt-6 text-center">
              <Link to="/" className="text-primary font-medium hover:underline">
                ← Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="container max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Welcome Back</h1>
          <p className="text-muted-foreground mt-2"> 
            Sign in to access your career insights
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-8">
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 mb-6 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )} 
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  autoComplete="email"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary font-medium hover:underline">
                Sign up
              </Link>
            </p>
          </div>

          {location.state && (location.state as LocationState).from && (
            <div className="mt-4 bg-blue-50 text-blue-700 p-3 rounded-md text-sm flex items-center gap-2">
              <Info className="h-4 w-4 flex-shrink-0" />
              <span>You need to log in to access {(location.state as LocationState).from?.pathname}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;